import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, Home, ChevronRight, Truck } from 'lucide-react';
import { OCCASION_DETAILS, PRODUCTS, SHOP_URL, CATALOG_URL } from '../mock';
import Reveal from '../components/Reveal';
import Seo, { SITE } from '../components/Seo';
import GiftAddons from '../components/GiftAddons';

function ProductCard({ p }) {
  return (
    <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-[#EFE8DD] hover:shadow-[0_20px_50px_rgba(61,81,66,0.15)] hover:-translate-y-1.5 transition-all duration-500">
      <div className="relative overflow-hidden aspect-square bg-[#F4EFE7]">
        <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        {p.tags.includes('Best seller') && (
          <span className="absolute top-3 left-3 bg-[#C4715A] text-white text-[11px] font-medium tracking-wide px-3 py-1 rounded-full">Best Seller</span>
        )}
        <span className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-md">
          <ArrowUpRight className="w-4.5 h-4.5 text-[#3D5142]" />
        </span>
      </div>
      <div className="p-4 flex items-center justify-between gap-2">
        <div>
          <h3 className="font-serif-display text-[17px] text-[#2A2A28] leading-tight">{p.name}</h3>
          <p className="text-[12px] text-[#8A7C6A] mt-0.5">Next-day delivery</p>
        </div>
        <span className="font-medium text-[16px] text-[#3D5142] whitespace-nowrap">${p.price}</span>
      </div>
    </a>
  );
}

export default function OccasionPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const detail = OCCASION_DETAILS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (detail) document.title = `${detail.name} | Delivery Florist Redwood City`;
    return () => { document.title = 'same day flower delivery Menlo Park|Redwood City'; };
  }, [slug, detail]);

  if (!detail) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif-display text-[40px] text-[#26302a]">Occasion not found</h1>
        <p className="mt-3 text-[#5A554C]">We couldn't find that collection.</p>
        <button onClick={() => navigate('/')} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#3D5142] text-white px-6 py-3"><ArrowLeft className="w-4 h-4" /> Back home</button>
      </div>
    );
  }

  const products = detail.productIds.map((id) => PRODUCTS.find((p) => p.id === id)).filter(Boolean);

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: detail.name, item: `${SITE}/occasions/${slug}` },
    ],
  };

  return (
    <>
      <Seo
        title={`${detail.name} | Delivery Florist Redwood City`}
        description={`${detail.description} Same-day delivery available across Redwood City, Menlo Park & the Bay Area.`}
        path={`/occasions/${slug}`}
        image={detail.img}
        jsonLd={breadcrumbLd}
      />
      {/* Occasion hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={detail.img} alt={detail.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#26302a]/85 via-[#26302a]/60 to-[#26302a]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-[13px] text-[#DCE3D6] mb-6">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-white transition-colors"><Home className="w-3.5 h-3.5" /> Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white">{detail.name}</span>
          </nav>
          <span className="text-[13px] tracking-[0.3em] uppercase text-[#D9A79C] font-medium">{detail.tagline}</span>
          <h1 className="font-serif-display text-[42px] sm:text-[54px] text-white mt-3 leading-[1.05] max-w-2xl">{detail.name}</h1>
          <p className="mt-5 text-[17px] text-[#E4E9DE] max-w-xl leading-relaxed">{detail.description}</p>
          <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#3D5142] hover:bg-[#F1ECE3] text-[16px] font-medium px-7 py-3.5 transition-colors shadow-lg">
            Order Now <ArrowUpRight className="w-4.5 h-4.5" />
          </a>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-2 text-[14px] text-[#3D5142] mb-8">
              <Truck className="w-4 h-4 text-[#C4715A]" /> Same-day delivery available — order before 2 PM
            </div>
            <h2 className="font-serif-display text-[32px] sm:text-[40px] text-[#26302a] leading-tight mb-10">Handpicked for {detail.name.replace(' Flowers', '').replace(' & Funeral', '')}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 80}><ProductCard p={p} /></Reveal>
            ))}
          </div>
          <div className="text-center mt-14">
            <a href={CATALOG_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#3D5142] text-[#3D5142] hover:bg-[#3D5142] hover:text-white text-[16px] font-medium px-8 py-3.5 transition-colors">
              See all flowers <ArrowUpRight className="w-4.5 h-4.5" />
            </a>
          </div>
        </div>
      </section>

      <GiftAddons />

      {slug === 'sympathy' && (
        <section data-testid="sympathy-guides-section" className="py-16 lg:py-20 bg-[#F7F3EB]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal>
              <h2 className="font-serif-display text-[28px] sm:text-[34px] text-[#26302a] leading-tight">Not sure what to send? These gentle guides can help</h2>
              <p className="mt-3 text-[15.5px] text-[#5A554C] max-w-2xl">Written by our designers, for the moments when you need clear, kind answers quickly.</p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { slug: 'funeral-flower-arrangements-guide', label: 'Funeral flower arrangements: the complete guide' },
                { slug: 'funeral-flower-arrangements-menlo-park', label: 'Funeral flowers in Menlo Park & nearby' },
                { slug: 'funeral-flower-arrangements-peninsula-funeral-homes', label: 'Delivery to Peninsula funeral homes' },
                { slug: 'funeral-flower-arrangements-cost', label: 'What funeral flowers cost (honest breakdown)' },
                { slug: 'sympathy-flowers-etiquette-guide', label: 'Sympathy etiquette: what to send & write' },
              ].map((g) => (
                <Link key={g.slug} to={`/blog/${g.slug}`} data-testid={`sympathy-guide-${g.slug}`} className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E1D7C7] bg-white px-5 py-4 hover:border-[#3D5142] transition-colors">
                  <span className="text-[15px] font-medium text-[#3D5142] group-hover:text-[#C4715A] transition-colors">{g.label}</span>
                  <ArrowUpRight className="w-4 h-4 shrink-0 text-[#C4715A]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
